import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Check, Loader2, Ruler } from "lucide-react";
import { getTerrariums, postEnquiry, scrollToSection } from "@/lib/site";
import { Reveal, SectionTag } from "@/components/Reveal";

const SPECS = [
  "Designet og håndbygget i beton — af os",
  "Indbyggede gange, afsatser og gemmesteder",
  "Du vælger selv planter, kork og indretning",
  "Kun afhentning: Fælleden 16, 7190 Billund",
];

const GALLERY = [
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_gekko-fuel/artifacts/9a96901c1e38163a_image.webp",
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_gekko-fuel/artifacts/2xttfv2s_image.webp",
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_gekko-fuel/artifacts/juwf6348_image.webp",
];

export default function Terrariums() {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const [spec, setSpec] = useState({ name: "", email: "", dims: "", note: "" });
  const [sendingSpec, setSendingSpec] = useState(false);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  useEffect(() => {
    getTerrariums().then(setItems).catch(() => {});
  }, []);

  const ready = items.find((t) => t.kind === "ready");

  const setSpecField = (k) => (e) => setSpec((s) => ({ ...s, [k]: e.target.value }));

  const submitSpec = async (e) => {
    e.preventDefault();
    if (!spec.name.trim() || !spec.email.trim() || !spec.dims.trim()) {
      toast.error("Udfyld venligst navn, e-mail og ønskede mål.");
      return;
    }
    setSendingSpec(true);
    try {
      await postEnquiry({
        name: spec.name,
        email: spec.email,
        topic: "Specialbygget terrarium",
        message: `Ønskede mål: ${spec.dims}\n${spec.note}`,
      });
      toast.success("Forespørgsel sendt — vi vender tilbage med et tilbud.");
      setSpec({ name: "", email: "", dims: "", note: "" });
    } catch {
      toast.error("Noget gik galt. Prøv igen.");
    } finally {
      setSendingSpec(false);
    }
  };

  const field =
    "w-full border border-white/15 bg-ink px-4 py-3 text-sm text-cream placeholder:text-sage/50 outline-none transition-colors duration-300 focus:border-forest";

  return (
    <section
      id="terrariums"
      data-testid="terrariums-section"
      ref={ref}
      className="px-6 py-24 lg:px-16 lg:py-36"
    >
      <Reveal>
        <SectionTag index="04" label="Habitatet" />
      </Reveal>
      <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12">
        <div className="relative overflow-hidden border border-white/10 lg:col-span-7">
          <motion.img
            src={GALLERY[0]}
            alt="Håndbygget beton-baggrund i Exo Terra"
            style={{ y, objectPosition: "center 42%" }}
            className="h-[420px] w-full scale-105 object-cover lg:h-[560px]"
          />
          <span className="absolute left-6 top-6 border border-white/20 bg-ink/50 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-pale backdrop-blur-md">
            Håndbygget af os
          </span>
        </div>

        <div className="flex flex-col justify-between lg:col-span-5">
          <Reveal>
            <h2 className="font-serif text-4xl tracking-tight text-cream sm:text-5xl">
              Håndbyggede beton-baggrunde
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-sage">
              Hver baggrund designer og håndbygger vi selv — skulptureret i beton,
              malet og forseglet i hånden. Det, du køber, er det unikke, håndlavede
              fundament med gange, afsatser og gemmesteder. Beplantning, kork og øvrig
              indretning står du selv for — så dit terrarium bliver helt dit eget.
            </p>
            <ul className="mt-8 space-y-3">
              {SPECS.map((s) => (
                <li key={s} className="flex items-center gap-3 text-sm text-cream/90">
                  <Check className="h-4 w-4 text-forest" strokeWidth={2} />
                  {s}
                </li>
              ))}
            </ul>
            <button
              data-testid="terrarium-enquire-button"
              onClick={() => {
                window.dispatchEvent(
                  new CustomEvent("enquiry-topic", {
                    detail: { topic: "Specialbygget terrarium", item: "Håndbygget beton-baggrund" },
                  })
                );
                scrollToSection("#enquiry");
              }}
              className="group mt-10 flex items-center gap-3 self-start rounded-full bg-forest px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-pale hover:text-ink"
            >
              Bestil specialbygning
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </button>
          </Reveal>

          {ready && (
            <Reveal delay={0.15}>
              <div
                data-testid="terrarium-ready-card"
                onClick={() => navigate(`/vare/${ready.slug}`)}
                className="mt-12 flex cursor-pointer items-center gap-5 border border-white/10 bg-surface p-4 transition-colors duration-300 hover:border-forest/60"
              >
                <img
                  src={ready.image}
                  alt={ready.name}
                  className="h-20 w-20 border border-white/10 object-cover"
                />
                <div className="flex-1">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-pale/70">
                    {ready.status}
                  </p>
                  <p className="mt-1 text-sm font-medium text-cream">{ready.name}</p>
                  <p className="mt-1 font-mono text-xs text-pale">{ready.price}</p>
                  {ready.variants?.length > 0 && (
                    <p className="mt-1 text-[11px] leading-snug text-sage">
                      Inkl. nyt Exo Terra 45×45×60 cm terrarium i prisen
                    </p>
                  )}
                </div>
                <button
                  data-testid="terrarium-customize-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/vare/${ready.slug}`);
                  }}
                  className="flex items-center gap-2 rounded-full bg-forest px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-pale hover:text-ink"
                >
                  Vælg tilvalg
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </Reveal>
          )}
        </div>
      </div>

      <Reveal>
        <div
          data-testid="custom-size-form"
          className="mt-16 border border-white/10 bg-moss p-8 lg:p-10"
        >
          <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-pale">
            <Ruler className="h-4 w-4 text-forest" />
            Specialmål
          </p>
          <h3 className="mt-4 font-serif text-3xl tracking-tight text-cream">
            Andre mål end 45×45×60?
          </h3>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-sage">
            Fortæl os hvilke mål du drømmer om, så vender vi tilbage med et uforpligtende
            tilbud på en baggrund bygget præcis til dig.
          </p>
          <form onSubmit={submitSpec} className="mt-8 grid gap-5 sm:grid-cols-2">
            <input
              data-testid="custom-size-name-input"
              value={spec.name}
              onChange={setSpecField("name")}
              placeholder="Dit navn"
              className={field}
            />
            <input
              data-testid="custom-size-email-input"
              type="email"
              value={spec.email}
              onChange={setSpecField("email")}
              placeholder="Din e-mail"
              className={field}
            />
            <input
              data-testid="custom-size-dims-input"
              value={spec.dims}
              onChange={setSpecField("dims")}
              placeholder="Ønskede mål (fx 60×45×90 cm)"
              className={`${field} sm:col-span-2`}
            />
            <textarea
              data-testid="custom-size-note-input"
              value={spec.note}
              onChange={setSpecField("note")}
              placeholder="Andre ønsker — gange, gemmesteder, vandfald…"
              rows={3}
              className={`${field} resize-none sm:col-span-2`}
            />
            <button
              data-testid="custom-size-submit-button"
              type="submit"
              disabled={sendingSpec}
              className="flex items-center justify-center gap-3 rounded-full bg-forest py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-pale hover:text-ink disabled:opacity-60 sm:col-span-2 sm:w-fit sm:px-10"
            >
              {sendingSpec ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send ønske"}
            </button>
          </form>
        </div>
      </Reveal>
    </section>
  );
}
